import type { GamePathValue } from './gamePaths';

export const di = {
  // TODO string "desktop-body" is hardcoded in other files, once migrated to vanilla-extract these should use this constant
  desktop: 'desktop-body',
  // handheld: 'handheld-body', in case we need this later
  ios: 'ios-body',
  ios26: 'ios-26-body',
  android: 'android-body',
  lobbyView: 'lobby-view',
} as const;

export const OS = {
  ANDROID: 'android',
  IOS: 'ios',
  PC: 'pc',
};

export const PLATFORM = {
  PHONE: 'phone',
  TABLET: 'tablet',
  DESKTOP: 'desktop',
};

export const FRAME = {
  IFRAME: 'iframe',
  POPUP: 'popup',
  FULL: 'full',
};

export const BROWSER = {
  CHROME: 'chrome',
  SAFARI: 'safari',
  EXPLORER: 'explorer',
  EDGE: 'edge',
  FIREFOX: 'firefox',
  OPERA: 'opera',
  UNKNOWN: 'unknown',
};

export const TYPE = {
  HTML: 'html',
  TABLET: 'tablet',
  SMARTPHONE: 'smartphone',
  MOBILE: 'mobile',
};

export const DEVICE = {
  OS,
  PLATFORM,
  FRAME,
  BROWSER,
  TYPE,
};

const desktopBodySelector = `body:is(.${di.desktop})` as const;
const handheldBodySelector = `body:not(.${di.desktop})` as const;
const iOSSelector = `body:is(.${di.ios})` as const;
const iOS26Selector = `body:is(.${di.ios26})` as const;
const androidSelector = `body:is(.${di.android})` as const;
// selector for global overrides when in game view (not in lobby)
const globalGameSelector = `body:not(.${di.lobbyView})` as const;
// selector for global overrides when in mobile game view (not in lobby)
const globalMobileGameSelector = `${handheldBodySelector}:not(.${di.lobbyView})` as const;

/**
   * device selector for targeting css to a specific type of device
   *
   * @example
   *
   * ```
   * const cssPerDeviceType = style({
   *   selectors: {
   *     [`${ds.desktopSelector} div.yellowColor &`]: {
   *       // css here will be applied only on desktop, on div having class="yellowColor"
   *       color: 'yellow',
   *     },
   *     [ds.desktop]: {
   *       // css here will be applied only on desktop
   *       color: 'blue',
   *     },
   *     [ds.handheld]: {
   *       // css here will be applied only on handheld
   *       color: 'red',
   *     },
   *     [ds.ios]: {
   *       // css here will be applied only on iOS
   *       color: 'white',
   *     },
   *   },
   * });
   * ```
   */
export const ds = {
  desktopSelector: desktopBodySelector,
  handheldSelector: handheldBodySelector,
  iOSSelector,
  iOS26Selector,
  androidSelector,
  desktop: `${desktopBodySelector} &`,
  handheld: `${handheldBodySelector} &`,
  ios: `${iOSSelector} &`,
  ios26: `${iOS26Selector} &`,
  android: `${androidSelector} &`,
  globalGameSelector,
  globalMobileGameSelector,
} as const;

/**
 * Creates CSS selectors scoped to a specific game via `data-game` attribute.
 * The extra attribute selector gives these higher specificity than the
 * base `ds.globalGameSelector` / `ds.globalMobileGameSelector` selectors,
 * so game overrides always win regardless of stylesheet injection order.
 *
 * @param gameSlug - Game slug from GAME_PATHS (e.g. 'andarbahar')
 */
export const createGameSelectors = (gameSlug: GamePathValue) => ({
  game: `body[data-game="${gameSlug}"]:not(.${di.lobbyView})` as const,
  gameMobile: `body[data-game="${gameSlug}"]:not(.${di.desktop}):not(.${di.lobbyView})` as const,
});
